export type LocaleCode =
  | 'en-us'
  | 'en-gb'
  | 'en-au'
  | 'fr-fr'
  | 'de-de'
  | 'es-es'
  | 'it-it'
  | 'pt-pt'
  | 'he-il'
  | 'ro-ro'
  | 'cs-cz'
  | 'fi-fi'
  | 'nl-nl'
  | 'pl-pl'
  | 'ar-xa';

export interface LocaleConfig {
  default: boolean;
  path: string;
  ogLang: string;
  /**
   * Explicit hreflang tag when the OG locale cannot be derived into a correct
   * BCP 47 value. Facebook's generic Arabic OG locale is `ar_AR`, but as an
   * hreflang `ar-AR` means "Arabic (Argentina)" — so ar-xa pins plain `ar`.
   * When absent, hreflang falls back to `ogLang` with `_` replaced by `-`.
   */
  hreflang?: string;
  /**
   * Document writing direction. Sourced exclusively here so the locale JSONs
   * stay text-only (no per-block `direction` metadata). Surfaced via the html
   * `dir` attribute by `src/site/components/Head.tsx` and into Prismic
   * rich-text blocks by `pageContentService.hydrateRichTextWithLocaleString`.
   */
  dir: 'ltr' | 'rtl';
}

export const i18nConfig: Record<LocaleCode, LocaleConfig> = {
  'en-us': {
    default: true,
    path: 'en',
    ogLang: 'en_US',
    dir: 'ltr',
  },
  'en-gb': {
    default: false,
    path: 'en-gb',
    ogLang: 'en_GB',
    dir: 'ltr',
  },
  'en-au': {
    default: false,
    path: 'en-au',
    ogLang: 'en_AU',
    dir: 'ltr',
  },
  'fr-fr': {
    default: false,
    path: 'fr',
    ogLang: 'fr_FR',
    dir: 'ltr',
  },
  'de-de': {
    default: false,
    path: 'de',
    ogLang: 'de_DE',
    dir: 'ltr',
  },
  'es-es': {
    default: false,
    path: 'es',
    ogLang: 'es_ES',
    dir: 'ltr',
  },
  'it-it': {
    default: false,
    path: 'it',
    ogLang: 'it_IT',
    dir: 'ltr',
  },
  'pt-pt': {
    default: false,
    path: 'pt',
    ogLang: 'pt_PT',
    dir: 'ltr',
  },
  'he-il': {
    default: false,
    path: 'he',
    ogLang: 'he_IL',
    dir: 'rtl',
  },
  'ro-ro': {
    default: false,
    path: 'ro',
    ogLang: 'ro_RO',
    dir: 'ltr',
  },
  'cs-cz': {
    default: false,
    path: 'cs',
    ogLang: 'cs_CZ',
    dir: 'ltr',
  },
  'fi-fi': {
    default: false,
    path: 'fi',
    ogLang: 'fi_FI',
    dir: 'ltr',
  },
  'nl-nl': {
    default: false,
    path: 'nl',
    ogLang: 'nl_NL',
    dir: 'ltr',
  },
  'pl-pl': {
    default: false,
    path: 'pl',
    ogLang: 'pl_PL',
    dir: 'ltr',
  },
  'ar-xa': {
    default: false,
    path: 'ar',
    ogLang: 'ar_AR',
    hreflang: 'ar',
    dir: 'rtl',
  },
};

/**
 * Supported locale codes for UI and routing.
 * Single source of truth for gatsby-node, linkResolver, and i18next.
 */
export const getSupportedLocales = (): LocaleCode[] => Object.keys(i18nConfig) as LocaleCode[];

/**
 * Default locale when none is specified.
 */
export const getDefaultLocale = (): LocaleCode => 'en-us';

/**
 * Normalizes any locale string to a supported LocaleCode.
 * Maps short codes (en, de) and API format (en-US) to resource keys (en-us, de-de).
 */
export const normalizeToLocaleCode = (locale: string | undefined): LocaleCode => {
  if (!locale) return getDefaultLocale();
  const normalized = locale.toLowerCase().replace('_', '-');
  const supported = getSupportedLocales();
  if (supported.includes(normalized as LocaleCode)) return normalized as LocaleCode;
  const langOnly = normalized.split('-')[0];
  const match = supported.find((l) => l.startsWith(langOnly));
  return match ?? getDefaultLocale();
};
